import { computed } from 'vue'
import { useTheme } from 'vuetify/framework'
import { THEME_STORAGE_KEY } from '../../plugins/vuetify'

const COLOR_ALIASES: Record<string, string> = {
  primary: 'primary',
  onPrimary: 'on-primary',
  accent: 'accent',
  onAccent: 'on-accent',
  secondary: 'secondary',
  success: 'success',
  warning: 'warning',
  danger: 'error',
  error: 'error',
  info: 'info',
  focus: 'focus',
  backgroundPrimary: 'background-primary',
  backgroundSecondary: 'background-secondary',
  backgroundElement: 'background-element',
  backgroundBorder: 'background-border',
  textPrimary: 'text-primary',
  textSecondary: 'text-secondary',
  textInverted: 'text-inverted',
  'background-primary': 'background-primary',
  'background-secondary': 'background-secondary',
  'background-element': 'background-element',
  'background-border': 'background-border',
  'text-primary': 'text-primary',
  'text-secondary': 'text-secondary',
  'text-inverted': 'text-inverted',
}

function resolveColorKey(name: string): string {
  return COLOR_ALIASES[name] ?? name
}

function hexToRgba(hex: string, alpha: number): string {
  const normalized = hex.replace('#', '')
  if (normalized.length !== 6 && normalized.length !== 3) {
    return hex
  }
  const full =
    normalized.length === 3
      ? normalized
          .split('')
          .map((c) => c + c)
          .join('')
      : normalized
  const r = parseInt(full.slice(0, 2), 16)
  const g = parseInt(full.slice(2, 4), 16)
  const b = parseInt(full.slice(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

export function useAppTheme() {
  const theme = useTheme()

  const currentPresetName = computed(() => theme.global.name.value)

  const getColor = (name: string): string => {
    const key = resolveColorKey(name)
    return theme.current.value.colors[key] ?? theme.current.value.colors[name] ?? name
  }

  const applyPreset = (preset: 'light' | 'dark') => {
    theme.global.name.value = preset
    localStorage.setItem(THEME_STORAGE_KEY, preset)
  }

  const colorToRgba = (color: string, alpha: number) => {
    if (color.startsWith('rgba(') || color.startsWith('rgb(')) {
      return color
    }
    return hexToRgba(color, alpha)
  }

  const setHSLAColor = (color: string, { a }: { a?: number } = {}) => {
    return colorToRgba(color, a ?? 1)
  }

  const colors = computed(() => theme.current.value.colors)

  return {
    currentPresetName,
    colors,
    getColor,
    applyPreset,
    colorToRgba,
    setHSLAColor,
  }
}

/** @deprecated Use useAppTheme — kept for migration compatibility */
export const useColors = useAppTheme
